import Badge from '@components/Badge';
import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PinFixedFill from '@icons/PinFixedFill';
import { EventRow } from '@models/event';
import { EventStatusBadgeColor } from '@models/event/extension';
import { Link } from '@stackflow/link';
import { pressActionEffect } from '@styles/mixin';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
    event: EventRow;
}

function EventItem({ event }:Props) {
    const {
        id, status, startAt, endAt, listBannerImageUrl, title, contentSummary, isPinned,
    } = event;
    const { t } = useTranslation('board');

    return (
        <Link activityParams={{ eventId: id }}
            activityName='EventActivity'
            css={[containerStyle, pressActionEffect]} >
            {isPinned && (
                <Pin>
                    <PinFixedFill size={20}
                        color={colors.white}/>
                </Pin>
            )}
            <Body spacing={12} fullWidth>
                <Badge variant='outline'
                    color={EventStatusBadgeColor[status]}>
                    {t(`eventStatus.${status}`)}
                </Badge>
                <TitleText size='xl' fontWeight='bold'>{title}</TitleText>
                <BannerImage src={listBannerImageUrl}
                    alt={t('eventImageAlt')}/>
                <ContentText color={colors.gray7}
                    as='div'
                    dangerouslySetInnerHTML={{ __html: contentSummary }}/>
            </Body>
            <Footer>
                <Text color={colors.gray6} >
                    {dayjs(startAt).format('MM.DD')}-{dayjs(endAt).format('MM.DD')}
                </Text>
            </Footer>
        </Link>
    );
}

const containerStyle = css`
    border: 1px solid var(--color-gray3);
    border-radius: 8px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
`;

const Body = styled(Column)`
    padding: 16px 12px;
`;

const BannerImage = styled(Image)`
    height: auto;
    width: 100%;
    object-fit: cover;
    border-radius: 8px;
`;

const ContentText = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;

const Footer = styled.div`
    border-top: 1px solid var(--color-gray3);
    display: flex;
    justify-content: center;
    padding: 8px 0;
    text-align: center;
    width: 100%;
`;

const Pin = styled.div`
    background-color: var(--color-gray7);
    padding: 8px 12px;
    width: 100%;
`;

const TitleText = styled(Text)`
    -webkit-box-orient: vertical;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    white-space: wrap;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    width: 100%;
`;

export default EventItem;
